import { Box, Group, Stack } from "@mantine/core"
import { IconPlus, IconUpload } from "@tabler/icons-react"
import { ButtonWithMenu, Loader } from "@tfg/ui"
import { Dispatch, SetStateAction, useState } from "react"
import { useTranslation } from "react-i18next"

import { TABLE_CONTEXT_MAP } from "@tfg/types/schemas/modules/view/constants/table-context-map"

import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import { Views } from "~/components/table/views/Views"
import { useFindSupplierMapping } from "~/gen/api"
import {
  CreateBusinessPartnerRequestDto,
  FindSupplierMappingQueryResponse,
  UpdateBusinessPartnerRequestDto,
} from "~/gen/api/types"
import { usePaginatedQuery } from "~/hooks/usePaginatedQuery"
import { SupplierToolBar } from "~/pages/Brand/Suppliers/SuppliersRepository/components/SupplierToolBar"
import { useColumns } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/columns.tsx"
import { useBuildBusinessPartnerFilters } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/useBuildBusinessPartnerFilters"
import { useBuildBusinessPartnerSorts } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/useBuildBusinessPartnerSorts"

export type { ContactFormType } from "~/pages/Brand/Suppliers/SuppliersRepository/supplierFormSchema"
export { formSchema } from "~/pages/Brand/Suppliers/SuppliersRepository/supplierFormSchema"

export type SupplierProps = {
  searchSupplier: string
  setInitialValues: Dispatch<
    SetStateAction<
      CreateBusinessPartnerRequestDto | UpdateBusinessPartnerRequestDto
    >
  >
  openDrawer: () => void
  setFormType: Dispatch<SetStateAction<"create" | "update">>
  setSearchSupplier: Dispatch<SetStateAction<string>>
  openAddSupplierModal: () => void
}

export function Suppliers({
  openDrawer,
  setInitialValues,
  setFormType,
  searchSupplier,
  setSearchSupplier,
  openAddSupplierModal,
}: SupplierProps) {
  const { t } = useTranslation("organizationSettings")
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false)

  const columns = useColumns({
    setInitialValues,
    openDrawer,
    setFormType,
  })
  const { filters } = useBuildBusinessPartnerFilters()
  const { sorts, setSorts, sortOptions } = useBuildBusinessPartnerSorts()

  const {
    data: suppliersData,
    isPending: isPendingSuppliersQuery,
    isFetching: isFetchingSuppliersQuery,
    setPageBasedPagination,
    pageBasedPagination,
    totalCount,
    totalPages,
  } = usePaginatedQuery<
    typeof useFindSupplierMapping,
    FindSupplierMappingQueryResponse
  >(useFindSupplierMapping, {
    searchSupplier,
    filters,
    sorts,
  })

  const suppliers = suppliersData?.data ?? []

  const table = useCustomReactTable({
    data: suppliers,
    columns,
    state: {
      pagination: pageBasedPagination,
    },
    pageCount: totalPages,
    manualPagination: true,
    enableRowSelection: true,
    onPaginationChange: setPageBasedPagination,
  })

  return (
    <Stack>
      <Views tableContext={TABLE_CONTEXT_MAP.BUSINESS_PARTNERS_REPOSITORY} />
      <Group justify="space-between">
        <Group justify="left">
          <SupplierToolBar
            table={table}
            isColumnManagerOpen={isColumnManagerOpen}
            setIsColumnManagerOpen={setIsColumnManagerOpen}
            searchSupplier={searchSupplier}
            setSearchSupplier={setSearchSupplier}
            sorts={sorts}
            setSorts={setSorts}
            sortOptions={sortOptions}
          />
        </Group>
        <ButtonWithMenu
          items={[
            {
              label: t("supplierSettings.addSupplier"),
              icon: (
                <Box pr="sm">
                  <IconPlus />
                </Box>
              ),
              onClick: () => openAddSupplierModal(),
            },
            {
              icon: (
                <Box pr="sm">
                  <IconUpload />
                </Box>
              ),
              label: t("supplierSettings.importSuppliers"),
              onClick: () => {
                window.open("/data-transfer/import", "_blank")
              },
            },
          ]}
        >
          {t("supplierSettings.dropdownButton.label")}
        </ButtonWithMenu>
      </Group>
      {isPendingSuppliersQuery ? (
        <Loader fullPage />
      ) : (
        <CustomTable
          table={table}
          manualPaginationCount={totalCount}
          setOpenColumnManager={setIsColumnManagerOpen}
          isFetching={isFetchingSuppliersQuery}
        />
      )}
    </Stack>
  )
}
